import {
    ArrayUtil, CurrencySettings, CurrencyUtil, ModelRow, Navigation, PermissionsUtil, StringUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, DataSource, Label, Layout, PanelProps, ReportData, ReportPreview, ReportPreviewTabSource, ReportType,
    Snackbar, Switch, Tab
} from "@mcleod/components";
import { ModelReportProperty } from "@mcleod/general/src/models/ModelReportProperty";
import {
    AutogenLayoutInvoiceProcessingPrintPostSlideout
} from "./autogen/AutogenLayoutInvoiceProcessingPrintPostSlideout";
import { InvoiceProcessingExceptionTable } from "./reports/InvoiceProcessingExceptionTable";
import { RowInvoiceProcessingProfile } from "./models/ModelInvoiceProcessingProfile";

export enum INVOICE_PROCESSING_STEP {
  EDIT_STEP = 0,
  PRINT_STEP = 1,
  PRINT_CONFIRM = 2,
  POST_STEP = 3,
  TRANSFER_EDIT_STEP = 4,
  TRANSFER_STEP = 5
}
export interface PrintPostData {
  order_id: string,
  billing_id: string,
  customer_id: string,
  is_split_pay: boolean
}

const BILL_TYPE_SUMMARY: string = "S";

const PRINT_ACTION_NO_AUTOMATIC: string = "N";
const PRINT_ACTION_FORCE: string = "D";
const PRINT_ACTION_FORCE_NO_DIALOG: string = "F";

export class InvoiceProcessingPrintPostSlideout extends AutogenLayoutInvoiceProcessingPrintPostSlideout {
    private _selectedList: PrintPostData[];
    private _profileRow: RowInvoiceProcessingProfile;
    private _buttonNextStep: Button;
    private _buttonOpenPayables: Button;
    private _allowedMinIndex: number = 0;
    private _allowedMaxIndex: number = 0;
    private _showTransferTab: boolean = false;
    private invoiceTotal;
    private invoiceCount:number = 0;
    private invoiceTotalUs;
    private invoiceTotalCa;
    private invoiceTotalMx;
    private invoiceCountUs: number = 0;
    private invoiceCountCa: number = 0;
    private invoiceCountMx: number = 0;
    private reportPreviewEditStep: ReportPreview;
    private reportPreviewPrintStep: ReportPreview;
    private reportPreviewPostStep: ReportPreview;
    private reportPreviewTransferEditStep: ReportPreview;
    private reportPreviewTransferStep: ReportPreview;
    private _switchPrintToImaging: Switch;

  constructor(panelProps?: Partial<PanelProps>) {
    super({
      id: "slideoutPrintPost",
      fillRow: true,
      fillHeight: true,
      padding: 8,
      ...panelProps
    });
    this.initializeButtonNextStep();
    this.initializeButtonOpenPayables();
  }

    override onLoad() {
        this.createEditStepReportPreview();
        this.createPrintStepReportPreview();
        this.createPostStepReportPreview();
        this.createTransferEditStepReportPreview();
        this.createTransferStepReportPreview();
    }

    private createEditStepReportPreview() {
        this.reportPreviewEditStep = new ReportPreview(
            { reportPreviewTitle: "Edit List" },
            { id: "reportPreviewEditStep" }
        );
        this.stepEditList.add(this.reportPreviewEditStep);
        this.reportPreviewEditStep.insertFirstRightHeaderTool(this.switchPrintToImaging);
    }

    private createPrintStepReportPreview() {
        this.reportPreviewPrintStep = new ReportPreview(
            { reportPreviewTitle: "Invoice Print Review" },
            { id: "reportPreviewPrintStep" }
        );
        // this.reportPreviewPrintStep.displaySingle
        this.stepPrintReview.add(this.reportPreviewPrintStep);
    }

    private createPostStepReportPreview() {
        this.reportPreviewPostStep = new ReportPreview(
            { reportPreviewTitle: "Posting Reports" },
            { id: "reportPreviewPostStep" }
        );
        this.stepPosting.add(this.reportPreviewPostStep);
    }

    private createTransferEditStepReportPreview() {
        this.reportPreviewTransferEditStep = new ReportPreview(
            { reportPreviewTitle: "Transfer To Settlement Edit List" },
            { id: "reportPreviewTransferEditStep" }
        );
        this.stepTransferToSettlementEdit.add(this.reportPreviewTransferEditStep);
    }

    private createTransferStepReportPreview() {
        this.reportPreviewTransferStep = new ReportPreview(
            { reportPreviewTitle: "Transfer To Settlement Reports" },
            { id: "reportPreviewTransferStep" }
        );
        this.stepTransferToSettlement.add(this.reportPreviewTransferStep);
    }

    private get switchPrintToImaging(): Switch {
        if (this._switchPrintToImaging == null) {
            this._switchPrintToImaging = new Switch({
                id: "switchPrintToImaging",
                caption: "Print To Imaging",
                checked: true,
                leftCaption: "No",
                rightCaption: "Yes",
                marginRight: 8,
                rowBreak: false
            });
        }
        return this._switchPrintToImaging;
    }

  public get selectedList(): PrintPostData[] {
    return this._selectedList;
  }

  public set selectedList(value: PrintPostData[]) {
    this._selectedList = value;
  }

  /** This is an event handler for the onClick event of buttonNextStep. */
  async nextStep() {
    this.buttonOpenPayables.visible = false;
    if (this.stepEditList.selected) {
      await this.printBills();
    } else if (this.stepPrintReview.selected) {
      await this.confirmPrint();
    } else if (this.stepPostConfirmation.selected) {
      await this.postInvoices();
    } else if (this.stepPosting.selected) {
      if (this.showTransferTab) {
        await this.transferEditToSettlements();
      } else {
        this.close();
      }
    } else if (this.stepTransferToSettlementEdit.selected) {
      if (this.showTransferTab) {
        await this.transferToSettlements();
        if (!PermissionsUtil.isUserDeniedLayout("lme/settlement/OrderPayablesMultiProfile")) {
          this.buttonOpenPayables.visible = true;
        }
      } else {
        this.close();
      }
    } else if (this.stepTransferToSettlement.selected) {
      this.close();
    }
  }
  private close() {
    this.slideOut();
  }
  async printBills(userSendToImagingResponse?: string) {
    this.updateStep(INVOICE_PROCESSING_STEP.PRINT_STEP);
    this.setButtonNextStepBusy(true);
    let sendToImaging = this.switchPrintToImaging.checked;
    if (userSendToImagingResponse === "Y") {
      sendToImaging = true;
    } else if (userSendToImagingResponse === "N") {
      sendToImaging = false;
    }

    this.labelLoadingImages.visible = sendToImaging;
    await this.sourceInvoicePrint.search({ order_list: this._selectedList, report_bill_type: this.profileRow.get("bill_type"), printed: false, print_to_imaging: sendToImaging, bill_status: "R"}).then(response => {
        this.invoiceTotal = response.modelRows[0].data["invoice_total"];
        this.invoiceCount = response.modelRows[0].data["invoice_count"];
        if (CurrencySettings.getSingleton()?.multiCurrencyEnabled()) {
            this.invoiceTotalUs = response.modelRows[0].data["invoice_amount_us"];
            this.invoiceTotalCa = response.modelRows[0].data["invoice_amount_ca"];
            this.invoiceTotalMx = response.modelRows[0].data["invoice_amount_mx"];
            this.invoiceCountUs = response.modelRows[0].data["invoice_count_us"];
            this.invoiceCountCa = response.modelRows[0].data["invoice_count_ca"];
            this.invoiceCountMx = response.modelRows[0].data["invoice_count_mx"];
        }
    });
    
    this.labelLoadingImages.visible = false;
    if (CurrencySettings.getSingleton()?.multiCurrencyEnabled()){
         this.labelAmountPrintedValue.visible = false;
        this.panelCa.visible = this.sourceInvoicePrint.activeRow?.get("invoice_count_ca") > 0;
        this.panelUs.visible = this.sourceInvoicePrint.activeRow?.get("invoice_count_us") > 0;
        this.panelMx.visible = this.sourceInvoicePrint.activeRow?.get("invoice_count_mx") > 0;
        //amounts
        const usAmount = this.sourceInvoicePrint.activeRow?.get("invoice_amount_us");
        this.labelInvoiceAmountUs.text =  usAmount != null ? CurrencyUtil.formatCurrency(usAmount) : "";

        const caAmount = this.sourceInvoicePrint.activeRow?.get("invoice_amount_ca");
        this.labelCaAmount.text =  caAmount != null ? CurrencyUtil.formatCurrency(caAmount) : "";

        const mxAmount = this.sourceInvoicePrint.activeRow?.get("invoice_amount_mx");
        this.labelInvoiceAmountMx.text =  mxAmount != null ? CurrencyUtil.formatCurrency(mxAmount) : "";

        //counts
        this.labelCountCa.text = "CAD (" + this.invoiceCountCa + ")";
        this.labelCountUs.text = "USD (" + this.invoiceCountUs + ")";
        this.labelCountMx.text = "MXN (" + this.invoiceCountMx + ")";
      } else {
          this.labelAmountPrintedValue.visible = true;
          const formattedAmount = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: this.invoiceTotal.currency_code
          }).format(this.invoiceTotal.amount);
          this.labelAmountPrintedValue.text = formattedAmount;
      }

    this.setButtonNextStepBusy(false);
  }
    async confirmPrint() {
        this.updateStep(INVOICE_PROCESSING_STEP.PRINT_CONFIRM);
        this.buttonIsBusy.visible = true;
        this.buttonIsBusy.busy = true;
        this.buttonNextStep.busy = true;
        await this.sourceInvoicePrint.search({ order_list: this._selectedList, print_to_imaging: false, printed: true, report_bill_type: this.profileRow.get("bill_type") });
        const labelCount = this.findComponentById("labelPostReviewInvoiceTotalAmount") as Label;
        labelCount.text = this.invoiceCount + "";
        const labelTotal = this.findComponentById("labelPostReviewAmountPrintedValue") as Label;
        if (CurrencySettings.getSingleton()?.multiCurrencyEnabled()) {
            this.labelAmountPrintedValue.visible = false;
            this.panelCaConfirm.visible = this.invoiceCountCa > 0;
            this.panelUsConfirm.visible = this.invoiceCountUs > 0;
            this.panelMxConfirm.visible = this.invoiceCountMx > 0;

            const usAmountConfirm = this.invoiceTotalUs;
            this.labelInvoiceAmountUsConfirm.text =  usAmountConfirm != null ? CurrencyUtil.formatCurrency(usAmountConfirm) : "";

            const caAmountConfirm = this.invoiceTotalCa;
            this.labelInvoiceCaAmountConfirm.text =  caAmountConfirm != null ? CurrencyUtil.formatCurrency(caAmountConfirm) : "";

            const mxAmountConfirm = this.invoiceTotalMx;
            this.labelInvoiceAmountMxConfirm.text =  mxAmountConfirm != null ? CurrencyUtil.formatCurrency(mxAmountConfirm) : "";

            this.labelCountCaConfirm.text = "CAD (" + this.invoiceCountCa + ")";
            this.labelCountUsConfirm.text = "USD (" + this.invoiceCountUs + ")";
            this.labelCountMxConfirm.text = "MXN (" + this.invoiceCountMx + ")";
        } else {
            this.labelAmountPrintedValue.visible = true;
            const formattedAmount = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: this.invoiceTotal.currency_code
            }).format(this.invoiceTotal.amount);
            labelTotal.text = formattedAmount;
            if (this.invoiceTotal.amount < 0) {
                labelTotal.color = "error";
            }
        }

        this.buttonIsBusy.visible = false;
        this.buttonIsBusy.busy = false;
        this.buttonNextStep.busy = false;
    }
  async postInvoices() {
    this.setButtonNextStepBusy(true);
    this.updateStep(INVOICE_PROCESSING_STEP.POST_STEP);
    await this.sourceInvoicePost.search({ order_list: this._selectedList, report_bill_type: this.profileRow.get("bill_type"), bill_status: "R" });
    this.setButtonNextStepBusy(false);
    this.buttonNextStep.caption = this.showTransferTab ? "Transfer to Settlement Edit" : "Close";
  }
  async transferToSettlements() {
    this.setButtonNextStepBusy(true);
    this.updateStep(INVOICE_PROCESSING_STEP.TRANSFER_STEP);
    await this.sourceTransferToSettlements.search({ order_list: this._selectedList, report_print_bill_type: this.profileRow.get("bill_type"), transfer_to_settlement: true });
    this.setButtonNextStepBusy(false);
    this.buttonNextStep.caption = "Close";
  }

  async transferEditToSettlements() {
    this.setButtonNextStepBusy(true);
    this.updateStep(INVOICE_PROCESSING_STEP.TRANSFER_EDIT_STEP);
    await this.sourceTransferToSettlements.search({ order_list: this._selectedList, report_print_bill_type: this.profileRow.get("bill_type"), transfer_to_settlement: true, post: false });
    this.setButtonNextStepBusy(false);
    this.buttonNextStep.caption = this.showTransferTab ? "Transfer to Settlement" : "Close";
  }

  public updateStep(step: number) {
    this.allowedMaxIndex = step;
    this.stepper2.selectedIndex = step;
  }

    /** This is an event handler for the afterExecution event of sourceInvoiceProcessing. */
    async sourceInvoiceProcessingAfterExecution() {
        this.reportPreviewEditStep.beginSearch();
        const row = this.sourceInvoiceProcessing.activeRow;
        const glReportData = this.createReportDataFromFields(row, "GL Distribution Edit List", "gl_report", "gl_excel_report");
        const editListReportData = this.createReportDataFromFields(row, "Invoicing Edit List", "edit_report", "edit_excel_report");
        const settlementTransferReportData = this.createReportDataFromFields(row, "(Pre-Posting) Agent Settlement Transfer", "settlement_report", "settlement_excel_report");
        const datafusionEditListReportData = this.createReportDataFromFields(row, "DataFusion Invoicing Edit List", "datafusion_report", "datafusion_excel_report");
        const exceptionTab = this.createExceptionTab(row?.get("exceptions_list"));
        const tabSources: ReportPreviewTabSource[] = [
            glReportData,
            editListReportData,
            settlementTransferReportData,
            datafusionEditListReportData,
            exceptionTab
        ];
        this.reportPreviewEditStep.endSearch(this.cleanTabSources(tabSources));
    }
  
  public get profileRow(): RowInvoiceProcessingProfile {
    return this._profileRow;
  }
  public set profileRow(value: RowInvoiceProcessingProfile) {
    this._profileRow = value;
  }

    /** This is an event handler for the afterExecution event of sourceInvoicePrint. */
    sourceInvoicePrintAfterExecution() {
        if (this.stepper2.selectedIndex == INVOICE_PROCESSING_STEP.PRINT_STEP) {
            this.reportPreviewPrintStep.beginSearch();
            const row = this.sourceInvoicePrint.activeRow;
            const printReviewReportData = this.createReportDataFromFields(row, "Invoice Print Review", "print_review_report", null);
            const tabSources: ReportPreviewTabSource[] = [ printReviewReportData ];
            this.reportPreviewPrintStep.endSearch(this.cleanTabSources(tabSources));

            if (row?.get("runtime_exceptions") || row?.get("exceptions_list")?.length > 0) {
                this.createPrintSnackbar(this.sourceInvoicePrint);
            }
            this.buttonNextStep.caption = "Confirm Print";
        } else if (this.stepper2.selectedIndex == INVOICE_PROCESSING_STEP.PRINT_CONFIRM) {
            this.buttonNextStep.caption = "Post";
        }
    }

  public get buttonNextStep(): Button {
    return this._buttonNextStep;
  }

  public setButtonNextStepBusy(value: boolean) {
    this._buttonNextStep.busy = value;
  }

  public get buttonOpenPayables(): Button {
    return this._buttonOpenPayables;
  }

  private initializeButtonNextStep() {
    this._buttonNextStep = new Button({
      id: "buttonNextStep",
      caption: "Generate Invoices",
      color: "primary.reverse",
      backgroundColor: "primary",
      visible: true,
      width: 140,
      height: 35,
      borderWidth: 1,
      rowBreak: false,
      borderColor: "primary",
      onClick: async () => {
        this.nextStep();
      }
    });
  }

  private initializeButtonOpenPayables() {
    this._buttonOpenPayables = new Button({
      id: "buttonOpenPayables",
      caption: "Open Order Payables",
      color: "primary.reverse",
      backgroundColor: "primary",
      visible: false,
      width: 140,
      height: 35,
      borderWidth: 1,
      rowBreak: false,
      borderColor: "primary",
      onClick: async () => {
        this.close();
        await this.openPayables();
      }
    });
  }

  async openPayables() {
    await Navigation.navigateTo("lme/settlement/OrderPayablesMultiProfile", { newTab: false });
  }


  /** This is an event handler for the afterExecution event of sourceInvoicePost. */
    sourceInvoicePostAfterExecution() {
        this.reportPreviewPostStep.beginSearch();
        const row = this.sourceInvoicePost.activeRow;
        const glReportData = this.createReportDataFromFields(row, "GL Distribution Post", "gl_report", "gl_excel_report");
        const postListReportData = this.createReportDataFromFields(row, "Invoicing Post List", "post_report", "post_excel_report");
        const settlementTransferReportData = this.createReportDataFromFields(row, "(Posting) Agent Settlement Transfer", "post_settlement_report", "post_settlement_excel_report");
        const datafusionEditListReportData = this.createReportDataFromFields(row, "DataFusion Invoicing Edit List", "post_datafusion_report", "post_datafusion_excel_report");
        const exceptionTab = this.createExceptionTab(row?.get("post_exceptions_list"));
        const tabSources: ReportPreviewTabSource[] = [
            glReportData,
            postListReportData,
            settlementTransferReportData,
            datafusionEditListReportData,
            exceptionTab
        ];
        this.reportPreviewPostStep.endSearch(this.cleanTabSources(tabSources));
    }

    /** This is an event handler for the afterExecution event of sourceTransferToSettlements. */
    sourceTransferToSettlementsAfterExecution() {
        const warning = this.sourceTransferToSettlements?.activeRow?.get("warning_message");
        const reportPreview = this.stepper2.selectedIndex === INVOICE_PROCESSING_STEP.TRANSFER_STEP ?
            this.reportPreviewTransferStep : this.reportPreviewTransferEditStep;
        this.handleTransferStep(warning, reportPreview);
    }

    private handleTransferStep(warning: string, reportPreview: ReportPreview) {
        if (warning) {
            Snackbar.showWarningSnackbar(warning);
        } else {
            reportPreview.beginSearch();
            const row = this.sourceTransferToSettlements.activeRow;
            const glReportData = this.createReportDataFromFields(row, "GL Distribution", "gl_report", "gl_excel_report");
            const settlementReportData = this.createReportDataFromFields(row, "Transfer to Settlement", "transfer_report", "transfer_excel_report");
            const settlementTransferReportData = this.createReportDataFromFields(row, "Transfer to Settlements Exceptions", "transfer_exception_report", "transfer_exception_excel_report");
            settlementTransferReportData.reportType = ReportType.ERROR;
            const tabSources: ReportPreviewTabSource[] = [
                glReportData,
                settlementReportData,
                settlementTransferReportData,
            ];
            reportPreview.endSearch(this.cleanTabSources(tabSources));
        }
    }

  public get allowedMaxIndex(): number {
    return this._allowedMaxIndex;
  }
  public set allowedMaxIndex(value: number) {
    this._allowedMaxIndex = value;
  }
  public get allowedMinIndex(): number {
    return this._allowedMinIndex;
  }
  public set allowedMinIndex(value: number) {
    this._allowedMinIndex = value;
  }

  public get showTransferTab(): boolean {
    return this._showTransferTab;
  }
  public set showTransferTab(value: boolean) {
    this._showTransferTab = value;
  }

  /** This is an event handler for the beforeStepListener event of stepper2. */
  stepper2BeforeStepListener(event) {
    const stepIndex = event.newIndex;
    const oldStepIndex = event.oldStep.index;

    if (stepIndex > this.allowedMaxIndex || stepIndex < this.allowedMinIndex) {
      event.preventDefault();
    }
    //temporary until we can persist totals/counts in preview
    // this.panelUs.visible = false;
    // this.panelCa.visible = false;
    // this.panelMx.visible = false;
  }

  async setupFromProfile() {
    if (!this.showTransferTab) {
      this.stepper2.removeStep(this.stepTransferToSettlement);
      this.stepper2.removeStep(this.stepTransferToSettlementEdit);
    }
    await this.setupReportProperty();
  }

  private async setupReportProperty() {
    let reportPropertyId: string;
    if (this.profileRow.get("bill_type") === BILL_TYPE_SUMMARY) {
      reportPropertyId = "Summary Freight Bill";
    } else {
      reportPropertyId = "Freight Bill";
    }
    const reportProperty = await new ModelReportProperty().searchSingle({ id: reportPropertyId });
    if (reportProperty) {
        switch (reportProperty.get("print_action")) {
            case PRINT_ACTION_NO_AUTOMATIC:
                this.switchPrintToImaging.enabled = false;
                this.switchPrintToImaging.checked = false;
                break;
            case PRINT_ACTION_FORCE:
            case PRINT_ACTION_FORCE_NO_DIALOG:
                this.switchPrintToImaging.enabled = false;
                this.switchPrintToImaging.checked = true;
                break;
        }
    }
  }

  private createPrintSnackbar(source: DataSource) {
    let message: string = "";
    if (source?.activeRow?.get("runtime_exceptions")) {
       message += source?.activeRow?.get("runtime_exceptions") + "\n\n";
    }
    if (source?.activeRow?.get("exceptions_list")?.length > 0) {
      source?.activeRow?.get("exceptions_list")?.forEach((exception) => {
        const orderId = exception[0];
        const customerId = exception[1];
        const billingId = exception[2];
        message += `Order ID: ${orderId}, Customer ID: ${customerId}, Billing ID: ${billingId}\n\n`;
      });
    }
    Snackbar.showWarningSnackbar(message);
  }

    private createReportDataFromFields(row: ModelRow, reportName: string, pdfBytesField: string, excelBytesField: string) {
        const result = new ReportData(reportName);
        if (!StringUtil.isEmptyString(pdfBytesField)) {
            result.pdfBytes = row?.get(pdfBytesField);
        }
        if (!StringUtil.isEmptyString(excelBytesField)) {
            result.excelBytes = row?.get(excelBytesField);
        }
        return result;
    }

    private createExceptionTab(exceptions: any[]): Tab {
        if (ArrayUtil.isEmptyArray(exceptions)) {
            return null;
        }
        const layoutExceptionTable =
            Layout.getLayout("lme/ar/reports/InvoiceProcessingExceptionTable") as InvoiceProcessingExceptionTable;
        layoutExceptionTable.addLayoutLoadListener(() => {
            layoutExceptionTable.data = exceptions;
        });
        const tab = new Tab({ components: [ layoutExceptionTable ], caption: "Invoice Exceptions" });
        tab.createSelector();
        tab.imageName = ImageName.triangleWarning;
        tab.imageColor = "error";
        tab.headingImage.rowBreak = false;
        return tab;
    }

    private cleanTabSources(tabSources: ReportPreviewTabSource[]): ReportPreviewTabSource[] {
        return tabSources.filter(data => data != null && !data.isEmpty());
    }
}
